/**
 * Phase 9.7 Q9 — BLUF version-diff strip (P0.5 "delta strip").
 *
 * CD R10 verdict Q9: "Ministers in a moving outbreak need delta, not state."
 * Renders above P1 Front page when version N differs from version N-1.
 *
 * v1 is sample-driven — pack-history persistence isn't built yet (Phase 11+
 * scope, sits alongside snapshot versioning's per-pack archive). The
 * primitive shapes the contract so when real history lands, the wiring
 * is a one-line change: swap `SAMPLE_DELTA` for a real diff against the
 * persisted previous pack.
 *
 * Three change classes per CD R10:
 *   - escalated:  metric got worse OR new threat surfaced (red)
 *   - improved:   metric got better OR risk dropped (green)
 *   - clarified:  qualitative refinement (no severity shift) (neutral)
 */

export type DeltaKind = 'escalated' | 'improved' | 'clarified';

export interface DeltaItem {
  kind: DeltaKind;
  /** Brief change description — "Cross-border risk: amber → red" etc. */
  text: string;
}

const KIND_META: Record<DeltaKind, { cssVar: string; icon: string; label: string }> = {
  escalated:  { cssVar: 'var(--bm-threat-1, #f87171)',                  icon: '↑', label: 'escalated' },
  improved:   { cssVar: 'var(--bm-prov-confirmed, var(--bm-state-live))', icon: '↓', label: 'improved' },
  clarified:  { cssVar: 'var(--bm-text-muted)',                          icon: '◆', label: 'clarified' },
};

/** Sample delta — illustrative. Real diff lands when pack-history register
 *  exists (Phase 11+). Mirrors the kinds of changes a real morning brief
 *  would show vs the prior evening brief on the same outbreak. */
export const SAMPLE_DELTA: readonly DeltaItem[] = [
  { kind: 'escalated', text: 'Cross-border risk: amber → red (4th jurisdiction confirmed)' },
  { kind: 'escalated', text: 'PHEIC proximity: 41% → 58% (+17pp in 18h)' },
  { kind: 'improved',  text: 'Vaccine supply: 12k → 18k doses on-hand (Belgium pre-positioning confirmed)' },
  { kind: 'clarified', text: 'BLUF likelihood: realistic possibility → likely (additional genomic-link case)' },
];

export interface BlufDeltaStripProps {
  /** Delta items to render. Defaults to SAMPLE_DELTA when omitted. */
  delta?: readonly DeltaItem[];
  /** Display label for the prior version — "yesterday 1800Z" / "Pack BM-2270" etc. */
  previousVersionLabel?: string;
}

export function BlufDeltaStrip({
  delta = SAMPLE_DELTA,
  previousVersionLabel = 'previous pack (BM-2270 · yesterday 1800Z)',
}: BlufDeltaStripProps) {
  if (delta.length === 0) return null;
  return (
    <div style={{
      marginBottom: 14, padding: '10px 14px',
      background: 'var(--bm-bg-subtle)',
      border: '1px solid var(--bm-border)',
      borderLeft: '3px solid var(--bm-accent, #6ea3ff)',
      borderRadius: 'var(--bm-radius-sm)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        gap: 10, marginBottom: 6, flexWrap: 'wrap' as const,
      }}>
        <span style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 700,
          letterSpacing: '.8px', color: 'var(--bm-accent, #6ea3ff)',
        }}>P0.5 · WHAT CHANGED SINCE {previousVersionLabel.toUpperCase()}</span>
        <span style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 10, color: 'var(--bm-text-subtle)',
        }}>{delta.length} change{delta.length === 1 ? '' : 's'}</span>
      </div>
      <ul style={{
        margin: '4px 0 0', paddingLeft: 0, listStyle: 'none',
        display: 'flex', flexDirection: 'column', gap: 4,
      }}>
        {delta.map((d, i) => {
          const m = KIND_META[d.kind];
          return (
            <li key={i} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              fontSize: 12, color: 'var(--bm-text)', lineHeight: 1.4,
            }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 18, fontFamily: 'var(--bm-font-mono)', fontSize: 11, fontWeight: 700,
                color: m.cssVar,
              }} title={m.label}>{m.icon}</span>
              <span>{d.text}</span>
            </li>
          );
        })}
      </ul>
      <div style={{
        marginTop: 8, fontFamily: 'var(--bm-font-mono)', fontSize: 9.5,
        color: 'var(--bm-text-subtle)',
      }}>
        ◇ Illustrative diff — pack-history persistence is Phase 11+; real diff activates when
        the previous pack is archived per the snapshot-versioning pattern.
      </div>
    </div>
  );
}
